
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import type {
  UserConfigurationState,
  Role,
  RateMultiplier,
  BillingStructureConfig,
  GearConfig,
} from '@/types';
import { initialOnboardingState as baseInitialState } from '@/constants/initialState';

// --- Types ---
type UserConfigState = UserConfigurationState;

type UserConfigDispatch = {
  updateProfile: (data: Partial<UserConfigurationState>) => void;
  updateBillingConfig: (data: Partial<UserConfigurationState> | ((prevState: UserConfigurationState) => Partial<UserConfigurationState>)) => void;
  updateBillingStructureConfig: (data: Partial<BillingStructureConfig>) => void;
  updateInvoiceConfig: (data: Partial<UserConfigurationState>) => void;
  updateRoles: (roles: Role[]) => void;
  addRole: (role: Role) => void;
  removeRole: (index: number) => void;
  updateRateMultipliers: (rateMultipliers: RateMultiplier[]) => void;
  updateTravelConfig: (data: Partial<UserConfigurationState>) => void;
  updateGearConfig: (data: Partial<GearConfig>) => void;
  updateCountryCodes: (codes: string[]) => void;
  setFullState: (newState: UserConfigurationState) => void;
};

// --- Contexts ---
const UserConfigStateContext = createContext<UserConfigState | undefined>(undefined);
const UserConfigDispatchContext = createContext<UserConfigDispatch | undefined>(undefined);

interface UserConfigProviderProps {
  children: React.ReactNode;
  /**
   * Pass `true` from the onboarding wizard so we start with
   * canned defaults (`initialOnboardingState`).
   * Leave `false` (or omit) when mounting in the normal Profile
   * flow – the real data will be loaded from the DB.
   */
  isOnboarding?: boolean;
  /** Optional override when you already fetched user data before mounting. */
  initialState?: Partial<UserConfigurationState>;
}

export const UserConfigProvider: React.FC<UserConfigProviderProps> = ({
  children,
  isOnboarding = false,
  initialState = {},
}) => {
  const startingState: UserConfigurationState = isOnboarding
    ? ({ ...baseInitialState, billingStructure: undefined } as unknown as UserConfigurationState)
    : ({ ...baseInitialState, ...initialState } as UserConfigurationState); // immediately replaced by DB load
  const [state, setState] = useState<UserConfigurationState>(startingState);
  const { user } = useAuth();

  // Pre-populate email from auth context only once on initial load
  const [hasInitializedEmail, setHasInitializedEmail] = useState(false);
  useEffect(() => {
    if (!hasInitializedEmail && user?.email) {
      setState(prev => ({
        ...prev,
        email: user.email ?? prev.email
      }));
      setHasInitializedEmail(true);
    }
  }, [user?.email, hasInitializedEmail]);

  const dispatchValue = useMemo<UserConfigDispatch>(() => ({
    updateProfile: (data) => {
      setState(prev => ({ ...prev, ...data }));
    },
    updateBillingConfig: (data) => {
      setState(prev => {
        const updates = typeof data === 'function' ? data(prev) : data;
        const newShortTurnConfig = updates.shortTurnConfig 
          ? { ...(prev.shortTurnConfig || {}), ...updates.shortTurnConfig } 
          : prev.shortTurnConfig;
        return { 
          ...prev, 
          ...updates,
          shortTurnConfig: newShortTurnConfig
        };
      });
    },
    updateBillingStructureConfig: (data) => {
      setState(prev => ({
        ...prev,
        billingStructureConfig: { ...prev.billingStructureConfig, ...data }
      }));
    },
    updateInvoiceConfig: (data) => {
      setState(prev => ({ ...prev, ...data }));
    },
    updateRoles: (roles) => {
      setState(prev => ({ ...prev, roles }));
    },
    addRole: (role) => {
      setState(prev => ({
        ...prev,
        roles: [...prev.roles, role]
      }));
    },
    removeRole: (index) => {
      setState(prev => {
        const updatedRoles = [...prev.roles];
        updatedRoles.splice(index, 1);
        return { ...prev, roles: updatedRoles };
      });
    },
    updateRateMultipliers: (rateMultipliers) => {
      setState(prev => ({ ...prev, rateMultipliers }));
    },
    updateTravelConfig: (data) => {
      setState(prev => ({ ...prev, ...data }));
    },
    updateGearConfig: (data) => {
      setState(prev => ({
        ...prev,
        gearConfig: { ...(prev.gearConfig ?? { gearItems: [], gearKits: [], cartage: { method: 'none' as const }, weekLengthDays: 5 }), ...data },
      }));
    },
    updateCountryCodes: (codes) => {
      setState(prev => ({ ...prev, countryCodes: codes }));
    },
    setFullState: (newState) => {
      setState(newState);
    },
  }), []);

  return (
    <UserConfigStateContext.Provider value={state}>
      <UserConfigDispatchContext.Provider value={dispatchValue}>
        {children}
      </UserConfigDispatchContext.Provider>
    </UserConfigStateContext.Provider>
  );
};

// --- Custom Hooks ---
// eslint-disable-next-line react-refresh/only-export-components
export const useUserConfig = () => {
  const state = useContext(UserConfigStateContext);
  const dispatch = useContext(UserConfigDispatchContext);
  if (!state || !dispatch) {
    throw new Error('useUserConfig must be used within a UserConfigProvider');
  }
  return { state, dispatch };
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUserConfigState = () => {
  const context = useContext(UserConfigStateContext);
  if (!context) {
    throw new Error('useUserConfigState must be used within a UserConfigProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUserConfigDispatch = () => {
  const context = useContext(UserConfigDispatchContext);
  if (!context) {
    throw new Error('useUserConfigDispatch must be used within a UserConfigProvider');
  }
  return context;
};

export type { Role, UserConfigurationState };
